<template>
  <tab-layout :full-width="true">
    <div slot="tabContent" class="bg-white rounded mb-4 p-3">
      <form-alert :message="alert.message.value" :type="alert.type.value" class="mb-3" />
      <div class="draws">

        <!-- Title bar -->
        <div class="drafts__title drafts__title_flex mb-4">
          <h1 class="h2 m-0">{{ displayEventName }}</h1>
          <div v-if="draftEvents.length > 1" class="draws-event-switch" @click.stop>
            <app-tooltip class="d-inline-block" :text="showEventSwitch ? '' : 'Switch to another event of this draft'">
              <button
                type="button"
                class="btn btn-link p-0 ml-2 dropdown-toggle draws-event-switch__btn"
                aria-label="Switch event"
                @click="showEventSwitch = !showEventSwitch"
              />
            </app-tooltip>
            <ul v-if="showEventSwitch" class="draws-event-switch__menu">
              <li v-for="e in draftEvents" :key="e.id">
                <router-link
                  :to="eventSwitchTarget(e)"
                  :class="{ 'draws-event-switch__item--active': String(e.id) === String(eventId) }"
                  @click.native="showEventSwitch = false"
                >{{ e.name }}</router-link>
              </li>
            </ul>
          </div>
          <a
            href="#"
            class="draws__download-table"
            :class="{ 'draws__download--loading': downloadingPrint === 0 }"
            target="_blank"
            @click.prevent="downloadPrint(0)"
          />
          <a
            href="#"
            class="draws__download-csv"
            :class="{ 'draws__download--loading': downloadingCsv }"
            target="_blank"
            @click.prevent="downloadCsv"
          />
          <div v-if="finalRoundExists" class="drafts__title-links">
            <template v-if="calculatorId">
              <router-link
                v-if="calculator && calculator.initialized"
                :to="'/prizes/' + eventId"
                class="btn btn-sm btn-outline-primary px-lg-3"
              >
                View results
              </router-link>
              <router-link
                :to="'/calculators/' + eventId"
                class="btn btn-sm btn-outline-primary px-lg-3"
              >
                Edit calculations
              </router-link>
            </template>
            <button
              v-else
              class="btn btn-sm btn-primary px-lg-3"
              type="button"
              :disabled="hasTiedWinners || actionLoading || isMarking"
              @click="onFinaliseEvent"
            >
              Finalise event and calculate prize money
            </button>
          </div>
        </div>

        <!-- Round tabs + search -->
        <tab-search-bar
          :items="roundTabItems"
          :active-tab="String(activeRound)"
          :search="search"
          placeholder="Competitor or horse"
          @tab-change="onRoundChange(Number($event))"
          @search-input="search = $event"
        />

        <!-- Round content -->
        <div class="draws-tabs__content-wrap">
          <div class="draws-tabs__content active">

            <!-- Unpublished warning -->
            <div v-if="unpublishedDrawsCount > 0" class="alert alert-info alert-simple">
              {{ unpublishedDrawsCount }} of the runs are not published.
              To add points, please publish the draw.
            </div>

            <!-- Publish button -->
            <button
              v-if="unpublishedDrawsCount > 0"
              class="btn btn-primary"
              type="button"
              :disabled="actionLoading || isMarking"
              @click="showPublishConfirm = true"
            >
              Publish the draw
            </button>

            <!-- Clean slate button (final round only) -->
            <div v-if="canResetTotalScore" class="pt-3">
              <button
                class="btn btn-outline-primary"
                type="button"
                :disabled="actionLoading || isMarking"
                @click="showCleanSlateConfirm = true"
              >
                Clean slate
              </button>
            </div>

            <!-- Re-generate button (round 1 only) -->
            <div v-if="activeRound === 1 && !isNextRoundGenerated" class="pt-3">
              <button
                class="btn btn-outline-primary"
                type="button"
                :disabled="actionLoading || isMarking"
                @click="showRegenerateConfirm = true"
              >
                Re-generate the draw
              </button>
            </div>

            <!-- Resend notifications button -->
            <div v-if="unpublishedDrawsCount === 0" class="pt-3">
              <button
                class="btn btn-outline-primary"
                type="button"
                :disabled="actionLoading || isMarking"
                @click="onResendNotifications"
              >
                Resend notifications
              </button>
            </div>

            <!-- Generate / Re-assign / Refund / Add nomination -->
            <div class="draws-generate bg-lighter border-top px-3 py-4 mt-4">
              <div class="draws-generate__row">
                <div v-if="currentRoundType === 1 || currentRoundType === 2" class="draws-generate__wrap">
                  <template v-if="!isCombinedNotSplit">
                    <form class="draws-generate__form pb-2" @submit.prevent="onGenerateNext">
                      <label for="draws-generate-number">{{ generateLabel }}</label>
                      <input
                        id="draws-generate-number"
                        v-model="generateScore"
                        class="draws-generate__input form-control"
                        type="number"
                        min="0"
                        placeholder="0"
                        @keydown="onPositiveIntKeydown"
                      >
                      <input
                        class="btn btn-primary draws-generate__btn"
                        type="submit"
                        value="Generate"
                        :disabled="actionLoading || isMarking"
                      >
                    </form>
                    <form
                      v-if="isParentSplit && activeRound === 1 && finalEvent"
                      class="draws-generate__form pb-2"
                      @submit.prevent="onGenerateInto(finalEvent.id)"
                    >
                      <label>Minimum score to go to {{ finalEvent.name }}</label>
                      <input
                        v-model="generateScores[finalEvent.id]"
                        class="draws-generate__input form-control"
                        type="number"
                        min="0"
                        placeholder="0"
                        @keydown="onPositiveIntKeydown"
                      >
                      <input
                        class="btn btn-primary draws-generate__btn"
                        type="submit"
                        value="Generate"
                        :disabled="actionLoading || isMarking"
                      >
                    </form>
                  </template>
                  <template v-else>
                    <form
                      v-for="child in childEvents"
                      :key="'gen-' + child.id"
                      class="draws-generate__form pb-2"
                      @submit.prevent="onGenerateInto(child.id)"
                    >
                      <label class="on-mobile-label">Minimum score to go into the {{ child.name }}</label>
                      <input
                        v-model="generateScores[child.id]"
                        class="draws-generate__input form-control"
                        type="number"
                        min="0"
                        placeholder="0"
                        @keydown="onPositiveIntKeydown"
                      >
                      <input
                        class="btn btn-primary draws-generate__btn"
                        type="submit"
                        value="Generate"
                        :disabled="actionLoading || isMarking"
                      >
                    </form>
                  </template>
                </div>
                <div class="draws-generate__reassign">
                  <router-link
                    :to="'/nominations/' + draftId + '/reassign-member'"
                    class="btn btn-primary"
                  >
                    Re-assign nominations
                  </router-link>
                </div>
                <div v-if="activeRound === 1" class="draws-generate__refund">
                  <button
                    class="btn btn-primary"
                    type="button"
                    :disabled="!existScratchedDraw || actionLoading || isMarking || refundQueueNotEmpty"
                    @click="showRefundConfirm = true"
                  >
                    Refund
                  </button>
                </div>
                <button
                  class="draws-generate__add-btn btn btn-outline-primary"
                  type="button"
                  :disabled="actionLoading || isMarking"
                  @click="showAddNomination = !showAddNomination"
                >
                  {{ addNomButtonLabel }}
                </button>
              </div>

              <!-- Add nomination form -->
              <transition name="slide-down">
              <div v-if="showAddNomination" class="draws-generate__adding">
                <div class="add-nomination">
                  <form class="nominations form" @submit.prevent="onAddNomination">
                    <div class="form-group">
                      <label class="add-horse__form-label">
                        Enter the competitor<br>
                        to {{ isFirstRoundInsert ? 'nominate' : 'restore' }} for
                      </label>
                      <app-multiselect
                        :value="nomMember"
                        :options="nomMemberOptions"
                        label="label"
                        track-by="id"
                        placeholder="Select competitor"
                        :searchable="true"
                        :loading="nomMemberLoading"
                        :internal-search="false"
                        :allow-empty="true"
                        @search-change="onMemberSearch"
                        @input="onMemberSelect"
                        @open="onMemberDropdownOpen"
                      >
                        <template v-if="!nomMemberLoading && nomMemberOptions.length === 0" slot="beforeList">
                          <li class="multiselect__element">
                            <span class="multiselect__option">{{ nomMemberEmptyMessage }}</span>
                          </li>
                        </template>
                      </app-multiselect>
                    </div>
                    <div class="form-group">
                      <label class="add-horse__form-label">
                        Choose the vacant<br>
                        place for nomination
                      </label>
                      <app-multiselect
                        :value="nomPlace"
                        :options="nomPlaceOptions"
                        label="label"
                        track-by="id"
                        placeholder="Select place"
                        :searchable="false"
                        :allow-empty="true"
                        @input="nomPlace = $event"
                      />
                    </div>
                    <div class="form-group">
                      <label class="add-horse__form-label">Choose the horse</label>
                      <app-multiselect
                        :value="nomHorse"
                        :options="nomHorseOptions"
                        label="label"
                        track-by="id"
                        placeholder="Select horse"
                        :searchable="true"
                        :loading="nomHorseLoading"
                        :allow-empty="true"
                        @input="nomHorse = $event"
                      >
                        <template v-if="!nomHorseLoading && nomHorseOptions.length === 0" slot="beforeList">
                          <li class="multiselect__element">
                            <span class="multiselect__option">{{ nomMember ? 'No horses found' : 'Select a competitor first' }}</span>
                          </li>
                        </template>
                      </app-multiselect>
                    </div>
                    <div v-if="showEventSelect" class="form-group">
                      <label class="add-horse__form-label">Choose event</label>
                      <app-multiselect
                        :value="nomEvent"
                        :options="nomEventOptions"
                        label="label"
                        track-by="id"
                        placeholder="Select event"
                        :searchable="false"
                        :allow-empty="true"
                        :disabled="isEventSelectIgnored"
                        @input="nomEvent = $event"
                      />
                      <small class="form-text text-muted">{{ nomEventHelpText }}</small>
                    </div>
                    <button
                      class="btn btn-primary"
                      type="submit"
                      :disabled="!nomMember || !nomHorse || !nomPlace || nomSubmitting"
                    >
                      {{ addNomButtonLabel }}
                    </button>
                  </form>
                </div>
              </div>
              </transition>

              <!-- Swap nominations -->
              <div class="d-flex justify-content-lg-end mt-lg-3">
                <button
                  class="draws-generate__add-btn btn btn-outline-primary"
                  type="button"
                  :disabled="actionLoading || isMarking"
                  @click="showSwapNominations = !showSwapNominations"
                >
                  Swap Nominations
                </button>
              </div>
              <transition name="slide-down">
              <div v-if="showSwapNominations" class="draws-nominations__swapping mt-3">
                <form class="d-flex" @submit.prevent="onSwap">
                  <div class="col col-lg-4 p-0">
                    <div v-if="swapCandidatesLoading" class="text-muted py-2">Loading candidates...</div>
                    <template v-else>
                      <div class="form-group">
                        <label class="add-horse__form-label">First Nomination</label>
                        <app-multiselect
                          :value="swapFrom"
                          :options="swapOptions"
                          label="label"
                          track-by="id"
                          placeholder="Select"
                          :searchable="true"
                          :allow-empty="true"
                          @input="swapFrom = $event"
                        />
                      </div>
                      <div class="form-group">
                        <label class="add-horse__form-label">Second Nomination</label>
                        <app-multiselect
                          :value="swapTo"
                          :options="swapOptions"
                          label="label"
                          track-by="id"
                          placeholder="Select"
                          :searchable="true"
                          :allow-empty="true"
                          @input="swapTo = $event"
                        />
                      </div>
                      <input
                        class="btn btn-primary"
                        type="submit"
                        value="Swap"
                        :disabled="!swapFrom || !swapTo || swapFrom.id === swapTo.id"
                      >
                    </template>
                  </div>
                </form>
              </div>
              </transition>
            </div>

            <!-- Run-off panel -->
            <div v-if="(currentRoundType === 3 || currentRoundType === 4) && hasTiedWinners && !(currentRoundType === 3 && showRunOffPanel)" class="mt-3">
              <div class="alert alert-warning text-center">
                <span class="draws-dashboard__warn-icon" />
                {{ currentRoundType === 4 ? 'There are some competitors that are tied for 1st after the completion of the RUN-OFF. Please, adjust the scores so that there is 1 winner.' : 'There are some competitors that are tied for 1st after the completion of the final round.' }}
              </div>
              <div class="text-center mt-2">
                <button class="btn btn-primary" type="button" :disabled="actionLoading || isMarking" @click="onGenerateRunOff">
                  Add a run-off
                </button>
              </div>
            </div>

            <!-- Draws table -->
            <div class="draws-table-wrap position-relative" style="min-height: 100px">
              <div v-if="actionLoading || loading" class="table-overlay">
                <div class="table-spinner" />
              </div>
              <div :class="['draws-table', { 'draws-table__content-last': currentRoundType !== 1 }]">
                <div class="draws-table__headers">
                  <div class="draws-table__header-main">
                    <div class="draws-table__row">
                      <div
                        :class="['draws-table__heading --runs sorting-enabled', { sorted: sortField === 'order' && sortDir === 'asc', 'sorted-desc': sortField === 'order' && sortDir === 'desc' }]"
                      >
                        <span @click="toggleSort('order')">Runs<button class="draws-table__sort" type="button" /></span>
                      </div>
                      <div
                        :class="['draws-table__heading --total-score sorting-enabled', { sorted: sortField === 'total' && sortDir === 'asc', 'sorted-desc': sortField === 'total' && sortDir === 'desc', 'extra_score_width': currentRoundType !== 1 }]"
                      >
                        <template v-if="currentRoundType === 1">
                          <span class="heading-desktop" @click="toggleSort('total')">
                            Round score<button class="draws-table__sort" type="button" />
                          </span>
                        </template>
                        <template v-else-if="currentRoundType === 3">
                          <span class="heading-desktop round-score">Final score</span>
                          <span class="heading-desktop" @click="toggleSort('totals_sum')">
                            Total score<button class="draws-table__sort" type="button" />
                          </span>
                        </template>
                        <template v-else-if="currentRoundType === 4">
                          <span class="heading-desktop round-score">Run-off score</span>
                          <span class="heading-desktop" @click="toggleSort('totals_sum')">
                            Total score<button class="draws-table__sort" type="button" />
                          </span>
                        </template>
                        <template v-else>
                          <span class="heading-desktop round-score">Round score</span>
                          <span class="heading-desktop" @click="toggleSort('totals_sum')">
                            Total score<button class="draws-table__sort" type="button" />
                          </span>
                        </template>
                        <span class="heading-mobile" @click="toggleSort('total')">
                          Sort by total<button class="draws-table__sort" type="button" />
                        </span>
                        <div class="draws-table__print" @click.stop="showPrintMenu = !showPrintMenu">
                          <div v-if="showPrintMenu" class="draws-table__print-dropdown" style="display: block">
                            <a href="#" @click.stop.prevent="downloadPrint(1)">
                              <span v-if="downloadingPrint === 1" class="draws-print-spinner mr-1" />Score Sheet Download
                            </a>
                            <a href="#" @click.stop.prevent="downloadPrint(2)">
                              <span v-if="downloadingPrint === 2" class="draws-print-spinner mr-1" />Announcer Download
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="draws-table__header-small">
                    <div class="draws-table__row">
                      <div :class="['draws-table__col --names', { 'extra_names_width': currentRoundType !== 1 }]">
                        <div class="draws-table__names-group">
                          <div class="draws-table__lead" />
                          <div class="draws-table__cell draws-table__cell--member draws-table__heading-small">
                            Competitor
                          </div>
                          <div class="draws-table__cell draws-table__cell--horse draws-table__heading-small">
                            Horse
                          </div>
                        </div>
                      </div>
                      <div class="draws-table__col --inputs">
                        <div class="draws-table__cell draws-table__heading-small">
                          Cutout
                        </div>
                        <div class="draws-table__cell draws-table__heading-small">
                          Horsework
                        </div>
                        <div class="draws-table__cell draws-table__heading-small">
                          Course
                        </div>
                      </div>
                      <div class="draws-table__col --total" />
                    </div>
                  </div>
                </div>
                <draggable
                  :value="filteredDraws"
                  class="draws-table__body"
                  handle=".drag-handle"
                  :animation="200"
                  @end="onDragEnd"
                >
                  <draw-row
                    v-for="draw in filteredDraws"
                    :key="draw.id"
                    :draw="draw"
                    :round-type="currentRoundType"
                    :pending-marks="pendingMarks"
                    :empty-slots="emptySlots"
                    :commit-options="commitOptions"
                    :score-limits="scoreLimits"
                    :judges-num="judgesNum"
                    :alert-message="rowAlerts[draw.id] != null ? rowAlerts[draw.id].message : null"
                    :alert-type="rowAlerts[draw.id] != null ? rowAlerts[draw.id].type : 'warning'"
                    @move="onMoveRequest($event)"
                    @commit="handleRowAction(draw.id, updateCommit($event.nominationId, $event.commit))"
                    @scratch="onScratch($event)"
                    @toggle-payment="handleRowAction(draw.id, togglePaymentStatus($event))"
                    @mark="onMark($event)"
                  />
                </draggable>

                <!-- No results -->
                <div
                  v-if="filteredDraws.length === 0"
                  class="alert alert-warning alert-simple rounded-0 m-0 w-100"
                >
                  There are no results.
                </div>
              </div>
              <div v-if="hasMore" class="text-center py-3">
                <button type="button" class="btn btn-outline-primary" :disabled="loadingMore" @click="loadMoreKeepingScroll">
                  {{ loadingMore ? 'Loading…' : 'Load more' }}
                </button>
              </div>
              <div ref="loadMoreTrigger" />
            </div>

          </div>
        </div>

      </div>

      <!-- Confirm modal -->
      <confirm-modal
        v-if="showConfirm"
        @confirm="showConfirm = false"
        @close="showConfirm = false"
      >
        Are you sure you want to perform this action?
      </confirm-modal>

      <!-- Refund confirm modal -->
      <modal v-if="showRefundConfirm" @close="showRefundConfirm = false">
        <div slot="body">
          <div class="alert alert-warning text-center mb-0">
            <strong>Warning!</strong>
            <p class="mb-0">Would you like to retain stock levy or fully refund the nominations?</p>
          </div>
        </div>
        <div slot="footer">
          <div class="d-flex justify-content-center">
            <button
              class="btn btn-primary"
              :disabled="refundLoading"
              @click="onRefund('retain-stock')"
            >
              Retain stock levy
            </button>
            <button
              class="btn btn-primary ml-2"
              :disabled="refundLoading"
              @click="onRefund('full')"
            >
              Refund fully
            </button>
          </div>
        </div>
      </modal>
      <!-- Publish confirm modal -->
      <confirm-modal
        v-if="showPublishConfirm"
        message="Draw will be published and competitors notified."
        @confirm="onPublish"
        @cancel="showPublishConfirm = false"
      />

      <!-- Re-generate confirm modal -->
      <confirm-modal
        v-if="showRegenerateConfirm"
        message="Warning you are about to regenerate the first round draw. If you proceed the entire draw will be redone and any entered scores from this round will be lost. If you proceed this can't be undone. Would you like to proceed?"
        @confirm="onRegenerate"
        @cancel="showRegenerateConfirm = false"
      />

      <!-- Clean slate confirm modal -->
      <confirm-modal
        v-if="showCleanSlateConfirm"
        message="Warning, all competitors in the final will have their score reset to 0."
        @confirm="onResetTotalScore"
        @cancel="showCleanSlateConfirm = false"
      />

      <!-- Scratch confirm modal -->
      <modal v-if="showScratchConfirm" @close="showScratchConfirm = false">
        <div slot="body">
          <div class="alert alert-warning text-center mb-0">
            <strong>Warning!</strong>
            <p class="mb-0">Would you like to alert the competitor that their run/s will be scratched?</p>
          </div>
        </div>
        <div slot="footer">
          <div class="d-flex justify-content-center">
            <button class="btn btn-primary" @click="onScratchConfirm(true)">Yes</button>
            <button class="btn btn-outline-primary ml-2" @click="onScratchConfirm(false)">No</button>
          </div>
        </div>
      </modal>

      <!-- Move/reorder notify confirm modal (monolith parity: same dialog for drag&drop and Move To) -->
      <modal v-if="showMoveNotifyConfirm" @close="showMoveNotifyConfirm = false">
        <div slot="body">
          <div class="alert alert-warning text-center mb-0">
            <strong>Warning!</strong>
            <p class="mb-0">Would you like to alert the competitor that their run will be moved?</p>
          </div>
        </div>
        <div slot="footer">
          <div class="d-flex justify-content-center">
            <button class="btn btn-primary" @click="onMoveNotifyConfirm(true)">Yes</button>
            <button class="btn btn-outline-primary ml-2" @click="onMoveNotifyConfirm(false)">No</button>
          </div>
        </div>
      </modal>
    </div>
  </tab-layout>
</template>

<script>
import { defineComponent, ref, reactive, computed, getCurrentInstance, watch, onMounted, onBeforeUnmount, nextTick, set } from '@vue/composition-api'
import TabLayout from '@/components/TabLayout'
import ConfirmModal from '@/components/ConfirmModal'
import Modal from '@/components/Modal'
import TabSearchBar from '@/components/TabSearchBar'
import AppTooltip from '@/components/AppTooltip'
import useDrawsEventView from '@/composables/draws/useDrawsEventView'
import useFormAlert from '@/composables/useFormAlert'
import FormAlert from '@/components/FormAlert'
import draggable from 'vuedraggable'
import DrawRow from '@/components/Draws/DrawRow'
import AppMultiselect from '@/components/AppMultiselect'
import nominationsApi from '@/services/api/nominations'
import drawsApi from '@/services/api/draws'
import EventBus from '@/store/EventBus'

export default defineComponent({
  name: 'DrawsEventView',
  components: {
    TabLayout,
    ConfirmModal,
    Modal,
    TabSearchBar,
    AppTooltip,
    FormAlert,
    DrawRow,
    draggable,
    AppMultiselect
  },
  setup() {
    const vm = getCurrentInstance()
    const route = vm.proxy.$route
    const draftId = computed(() => vm.proxy.$route.params.draftId)
    const eventId = computed(() => vm.proxy.$route.params.eventId)

    const alert = useFormAlert()

    const rowAlerts = reactive({})
    const rowTimers = {}
    const actionLoading = ref(false)
    const pendingMarks = reactive({})
    const pendingMarkCount = ref(0)
    const isMarking = computed(() => pendingMarkCount.value > 0)
    let actionCount = 0
    let refreshTieCheckFn = () => {}

    function handleRowAction(drawId, promise, markField) {
      if (!markField) {
        actionCount += 1
        actionLoading.value = true
      } else {
        const key = `${drawId}_${markField}`
        set(pendingMarks, key, true)
        pendingMarkCount.value += 1
      }
      promise.then((result) => {
        if (result == null || result.message == null) return
        set(rowAlerts, drawId, result)
        clearTimeout(rowTimers[drawId])
        rowTimers[drawId] = setTimeout(() => {
          set(rowAlerts, drawId, null)
        }, 5000)
      }).finally(() => {
        if (markField) {
          const key = `${drawId}_${markField}`
          set(pendingMarks, key, false)
          pendingMarkCount.value -= 1
          if (pendingMarkCount.value <= 0) {
            pendingMarkCount.value = 0
            refreshTieCheckFn()
          }
        } else {
          actionCount -= 1
          if (actionCount <= 0) {
            actionCount = 0
            actionLoading.value = false
          }
        }
      })
    }

    const search = ref('')
    const showConfirm = ref(false)
    const showAddNomination = ref(false)
    const showSwapNominations = ref(false)
    const swapFrom = ref(null)
    const swapTo = ref(null)
    const showPrintMenu = ref(false)
    const showRefundConfirm = ref(false)
    const refundLoading = ref(false)
    const showPublishConfirm = ref(false)
    const showRegenerateConfirm = ref(false)
    const showCleanSlateConfirm = ref(false)
    const generateScore = ref(null)
    const generateScores = ref({})
    const showScratchConfirm = ref(false)
    const pendingScratchDraw = ref(null)
    const showMoveNotifyConfirm = ref(false)
    const pendingMoveAction = ref(null)
    const nomMember = ref(null)
    const nomHorse = ref(null)
    const nomPlace = ref(null)
    const nomEvent = ref(null)
    const nomMemberOptions = ref([])
    const nomHorseOptions = ref([])
    const nomMemberLoading = ref(false)
    const nomMemberSearched = ref(false)
    const nomHorseLoading = ref(false)
    const nomSubmitting = ref(false)

    const {
      rounds,
      draws,
      emptyDrawsOrders,
      emptySlots,
      swapCandidates,
      swapCandidatesLoading,
      unpublishedDrawsCount,
      isNextRoundGenerated,
      paymentMethods,
      disabledPaymentMethods,
      loading,
      loadingMore,
      hasMore,
      redirectTo,
      activeRound,
      eventName,
      judgesNum,
      fetchAll,
      fetchSwapCandidates,
      loadMore,
      refreshDraws,
      refreshDrawsAndMeta,
      markDraw,
      deleteDraw,
      moveDraw,
      swapDraws,
      refundDraws,
      fetchRefundStatus,
      existScratchedDraw,
      draftEvents,
      refundQueueNotEmpty,
      isBrotherCombSplit,
      isParentSplit,
      parentEventId,
      childrenEventListWithParent,
      finalEvent,
      eventRoundsNum,
      lastRoundIndex,
      finalRoundExists,
      calculatorId,
      calculator,
      showRunOffPanel,
      hasTiedWinners,
      refreshTieCheck,
      showNotificationsModal,
      insertDraw,
      restoreDraw,
      cleanSlate,
      publishRound,
      resetTotalScore,
      resendNotifications,
      updateStatus,
      setRound,
      reorderDraw,
      regenerateFirstRound,
      generateRunOff,
      createCalculator,
      generateNext,
      generateInto,
      updateCommit,
      togglePaymentStatus,
      scoreLimits
    } = useDrawsEventView(eventId, draftId, route.params.round)

    refreshTieCheckFn = refreshTieCheck

    const router = vm.proxy.$router

    const swapOptions = computed(() => swapCandidates.value.map((c) => {
      let label = `${c.order + 1}. ${c.member_name || 'Empty'}`
      if (c.horse_name) label += ` (${c.horse_name})`
      return { id: c.id, label }
    }))

    const currentRoundType = computed(() => {
      const r = rounds.value.find(rd => rd.index === activeRound.value - 1)
      return r != null ? r.type : 1
    })

    const isFinalRound = computed(() => currentRoundType.value === 3)

    // A standalone split-combined final has one round but carries scores from its source events,
    // so it needs Clean Slate just like the final round of a multi-round event.
    const canResetTotalScore = computed(() => (
      isFinalRound.value
      && unpublishedDrawsCount.value > 0
      && (eventRoundsNum.value > 1 || isBrotherCombSplit.value)
    ))

    const generateLabel = computed(() => {
      const total = eventRoundsNum.value
      if (activeRound.value + 1 < total) return 'Minimum score to go into the next round'
      return 'Minimum score to go into the Final'
    })

    const hasChildrenEvents = computed(() => {
      const list = childrenEventListWithParent.value
      return list.length > 1
    })

    const isCombinedNotSplit = computed(() => hasChildrenEvents.value && !isParentSplit.value)

    // Event switcher next to the title: keeps the kind of round (the shared first round stays on
    // round 1, a final stays on the target's last round).
    const showEventSwitch = ref(false)

    function eventSwitchTarget(e) {
      const round = activeRound.value > 1 ? Math.max(e.rounds, 1) : 1
      return `/draws/${draftId.value}/${e.id}/${round}`
    }

    function closeEventSwitch() {
      showEventSwitch.value = false
    }

    const displayEventName = computed(() => {
      if (isCombinedNotSplit.value && activeRound.value === 1) {
        const parent = childrenEventListWithParent.value[0]
        return parent != null ? parent.name : eventName.value
      }
      return eventName.value
    })

    const childEvents = computed(() => {
      if (!hasChildrenEvents.value) return []
      return childrenEventListWithParent.value.slice(1)
    })

    const isFirstRoundInsert = computed(() => activeRound.value === 1 && !isBrotherCombSplit.value)
    const addNomButtonLabel = computed(() => (isFirstRoundInsert.value ? 'Add new nomination' : 'Restore nomination'))
    const nomMemberEmptyMessage = computed(() => (
      isFirstRoundInsert.value && !nomMemberSearched.value
        ? 'Type at least 3 characters to search'
        : 'No competitors found'
    ))

    const nomPlaceOptions = computed(() => emptySlots.value.map(s => ({
      id: s.id,
      label: String(s.order)
    })))

    const nomEventOptions = computed(() => {
      const list = childrenEventListWithParent.value
      const isSplit = isParentSplit.value
      const isRound1 = activeRound.value === 1
      const fe = finalEvent.value
      const evName = eventName.value

      return list
        .filter((e, idx) => {
          // restrict_single_nomination blocks creating a brand-new standalone nomination in this
          // child (NominationService::loadAndAssertEvent) — only relevant for Insert. For Restore
          // the competitor already has an existing nomination filed under this child (e.g. a
          // combined-nominated Maiden + Maiden For Maiden pair) and picking it here just tells
          // DrawService::restore which one to link the draw row to — hiding it made a valid
          // restore target impossible to select (see task-CAM-1484.md).
          if (isFirstRoundInsert.value && e.restrict_single_nomination) return false
          if (isSplit && isRound1 && idx === 2) return false
          // The parent is a valid choice for Insert (round 1) — selecting it there is exactly
          // how a combined nomination across all children gets created (DrawService::insert
          // resolves selected_event_id's own children). For Restore it's meaningless — a
          // nomination's event_id is never the parent, so restoring "into" it can never find
          // one (see task-CAM-1432.md) — hide it there, except the round-1 split merge case,
          // where this same list entry is relabelled below into "this child & final", not the
          // raw parent.
          if (!isFirstRoundInsert.value && e.id === parentEventId.value && !(isSplit && isRound1)) return false
          return true
        })
        .map((e) => {
          let label = e.name
          if (isSplit && isRound1) {
            if (fe && e.id === parentEventId.value) {
              label = `${evName} & ${fe.name}`
            } else {
              label = evName
            }
          }
          return { id: e.id, label }
        })
    })

    // A genuine split child (isParentSplit, not the final) is fully independent at round 2+ — no
    // shared pool with its sibling, so there's nothing meaningful to choose for Restore and picking
    // the wrong sibling would misfile it. Round 1 insert keeps the picker (it has its own
    // this-child/this-child+final merge option, see nomEventOptions). Combined-not-split (shared
    // pool) and the split-comb final (nomination can come from either child) always need it.
    const showEventSelect = computed(() => (
      childrenEventListWithParent.value.length > 0
      && (isFirstRoundInsert.value || !isParentSplit.value || isBrotherCombSplit.value)
    ))

    // Restore parity (DrawService::restore): filling a specific vacant place is a plain UPDATE
    // that never touches the slot's own event — "Choose event" can't override where it lands or
    // which nomination it's linked to once a specific place is picked. It only matters when
    // restoring to "Last" (a brand-new row, where it decides the event). Insert (round 1) always
    // keeps it live regardless of place, since there it also drives combined-nomination creation.
    const isEventSelectIgnored = computed(() => (
      !isFirstRoundInsert.value && !!nomPlace.value && nomPlace.value.id !== 'last'
    ))

    const nomEventHelpText = computed(() => {
      if (isFirstRoundInsert.value) {
        return 'Determines which event(s) this nomination is created for.'
      }
      return isEventSelectIgnored.value
        ? 'Ignored — the vacant place you picked already fixes the event. Only used when restoring to "Last".'
        : 'Only affects restoring to "Last" — has no effect once a specific vacant place is picked above.'
    })

    let memberSearchTimer = null

    // Secretary/admin hint: why an otherwise-unfinancial member is nominatable (CAM-1485)
    function memberVolunteerLabel(name, m) {
      if (m && m.is_volunteer) return `${name} (Volunteer)`
      if (m && m.is_volunteer_family) return `${name} (Volunteer family)`
      return name
    }

    // Restore candidate pool is scoped server-side to the previous round, so it's small enough
    // to show up front (first N) without requiring the user to type anything first.
    function fetchRestoreMembers(term) {
      return drawsApi.memberSearch(eventId.value, activeRound.value - 1, { term: term || undefined })
        .then(res => res.data.data.map(m => ({ id: m.id, label: memberVolunteerLabel(m.text, m) })))
    }

    function onMemberDropdownOpen() {
      if (isFirstRoundInsert.value || nomMemberSearched.value) return
      nomMemberLoading.value = true
      fetchRestoreMembers('')
        .then((options) => {
          nomMemberOptions.value = options
          nomMemberSearched.value = true
        })
        .catch(() => { nomMemberOptions.value = [] })
        .finally(() => { nomMemberLoading.value = false })
    }

    function onMemberSearch(term) {
      if (!isFirstRoundInsert.value) {
        clearTimeout(memberSearchTimer)
        memberSearchTimer = setTimeout(() => {
          nomMemberLoading.value = true
          fetchRestoreMembers(term)
            .then((options) => {
              nomMemberOptions.value = options
              nomMemberSearched.value = true
            })
            .catch(() => { nomMemberOptions.value = [] })
            .finally(() => { nomMemberLoading.value = false })
        }, 400)
        return
      }

      if (!term || term.length < 3) {
        nomMemberOptions.value = []
        nomMemberSearched.value = false
        return
      }
      clearTimeout(memberSearchTimer)
      memberSearchTimer = setTimeout(() => {
        nomMemberLoading.value = true
        nominationsApi.membersSearch({ q: term })
          .then((res) => {
            nomMemberOptions.value = res.data.data.map(m => ({ id: m.id, label: memberVolunteerLabel(m.full_name, m) }))
            nomMemberSearched.value = true
          })
          .catch(() => { nomMemberOptions.value = [] })
          .finally(() => { nomMemberLoading.value = false })
      }, 400)
    }

    function onMemberSelect(member) {
      nomMember.value = member
      nomHorse.value = null
      nomHorseOptions.value = []
      if (!member) return
      nomHorseLoading.value = true
      if (isFirstRoundInsert.value) {
        const horseEventId = parentEventId.value != null ? parentEventId.value : eventId.value
        nominationsApi.memberHorses(member.id, { event_id: horseEventId, for_draws: 1 })
          .then((res) => {
            const horses = res.data.data.horses != null ? res.data.data.horses : []
            nomHorseOptions.value = horses.map(h => ({
              id: h.id,
              label: h.disabled_reason ? `${h.name} (${h.disabled_reason})` : h.name,
              $isDisabled: !!h.disabled
            }))
          })
          .catch(() => { nomHorseOptions.value = [] })
          .finally(() => { nomHorseLoading.value = false })
      } else {
        // Restore (round > 0): candidates are horses this member ran in the previous round,
        // scoped server-side via the round-aware endpoint (parity with monolith getAllHorsesInEvent).
        drawsApi.memberHorses(eventId.value, activeRound.value - 1, member.id)
          .then((res) => {
            nomHorseOptions.value = res.data.data.map(h => ({ id: h.id, label: h.name }))
          })
          .catch(() => { nomHorseOptions.value = [] })
          .finally(() => { nomHorseLoading.value = false })
      }
    }

    function resetNomForm() {
      nomMember.value = null
      nomHorse.value = null
      nomPlace.value = null
      nomEvent.value = null
      nomMemberOptions.value = []
      nomHorseOptions.value = []
      nomMemberSearched.value = false
    }

    watch(showAddNomination, (val) => {
      if (val) resetNomForm()
    })

    async function onAddNomination() {
      if (!nomMember.value || !nomHorse.value || !nomPlace.value) return
      nomSubmitting.value = true
      let result
      if (isFirstRoundInsert.value) {
        const payload = {
          draw_id: nomPlace.value.id,
          member_id: nomMember.value.id,
          horse_id: nomHorse.value.id
        }
        if (nomEvent.value) {
          payload.selected_event_id = nomEvent.value.id
        }
        if (isParentSplit.value) {
          payload.current_event_id = eventId.value
        }
        result = await insertDraw(payload)
      } else {
        const drawId = nomPlace.value.id
        const payload = {
          member_id: nomMember.value.id,
          horse_id: nomHorse.value.id,
          event_id: eventId.value,
          round: activeRound.value - 1
        }
        if (nomEvent.value) {
          payload.selected_event_id = nomEvent.value.id
        }
        result = await restoreDraw(drawId, payload)
      }
      nomSubmitting.value = false
      if (result != null && result.message != null) {
        if (result.type === 'success') {
          alert.showSuccess(result.message)
          showAddNomination.value = false
        } else {
          alert.showError(result.message)
        }
      }
    }

    const roundTabItems = computed(() => rounds.value
      .filter(r => r != null)
      .map(r => ({
        value: String(r.index + 1),
        label: r.label
      })))

    const commitOptions = ref([])
    nominationsApi.getCommits().then((res) => { commitOptions.value = res.data.data })

    const sortField = ref('order')
    const sortDir = ref('asc')

    function toggleSort(field) {
      if (sortField.value === field) {
        sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
      } else {
        sortField.value = field
        sortDir.value = field === 'order' ? 'asc' : 'desc'
      }
    }

    const filteredDraws = computed(() => {
      const q = search.value.toLowerCase().trim()
      let list = draws.value
      if (q) {
        list = list.filter((d) => {
          const member = d.member
          const memberName = member != null
            ? `${member.first_name} ${member.last_name}`.toLowerCase()
            : ''
          const horseName = d.horse != null ? d.horse.name.toLowerCase() : ''
          return memberName.indexOf(q) !== -1 || horseName.indexOf(q) !== -1
        })
      }
      return list.slice().sort((a, b) => {
        const av = a[sortField.value] != null ? a[sortField.value] : 0
        const bv = b[sortField.value] != null ? b[sortField.value] : 0
        return sortDir.value === 'asc' ? av - bv : bv - av
      })
    })

    function onMark(event) {
      const drawId = event.drawId
      const judge = event.data.judges[0]
      const fieldType = judge.marks[0].type
      const markField = `${judge.judgeNumber}_${fieldType}`
      const scoreTypes = ['cutout', 'work', 'course']
      const preserve = {}
      scoreTypes.forEach((t) => {
        const key = `${drawId}_${judge.judgeNumber}_${t}`
        if (pendingMarks[key] && t !== fieldType) {
          if (!preserve[drawId]) preserve[drawId] = []
          preserve[drawId].push(t)
        }
      })
      handleRowAction(drawId, markDraw(drawId, event.data, Object.keys(preserve).length > 0 ? preserve : null), markField)
    }

    async function onFinaliseEvent() {
      actionLoading.value = true
      const result = await createCalculator()
      actionLoading.value = false
      if (result.type !== 'success') {
        alert.showError(result.message)
        return
      }
      router.push(`/calculators/${eventId.value}`)
    }

    // After a successful generation, open the round that was just generated.
    // dbRound is 0-based; URL/tab rounds are 1-based.
    function goToGeneratedRound(dbRound, targetEventId) {
      const newRound = dbRound + 1
      if (targetEventId != null && Number(targetEventId) !== Number(eventId.value)) {
        router.push(`/draws/${draftId.value}/${targetEventId}/${newRound}`)
        return
      }
      // Combined events: the generated round may live on a sibling/child event
      // (same rule as onRoundChange tab clicks).
      const roundData = rounds.value.find(r => r.index === dbRound)
      if (roundData && roundData.eventId && roundData.eventId !== Number(eventId.value)) {
        router.push(`/draws/${draftId.value}/${roundData.eventId}/${newRound}`)
        return
      }
      if (Number(activeRound.value) !== newRound) {
        setRound(newRound)
      } else {
        fetchAll(newRound)
      }
    }

    async function onGenerateNext() {
      actionLoading.value = true
      const result = await generateNext(generateScore.value != null ? Math.max(0, Number(generateScore.value)) : null)
      generateScore.value = null
      if (result && result.message) {
        if (result.type === 'success') alert.showSuccess(result.message)
        else alert.showError(result.message)
      }
      if (result && result.type === 'success') {
        // Always open the generated round; fall back to the round after the
        // active one when the API response carries no round index.
        goToGeneratedRound(result.round != null ? result.round : activeRound.value)
      } else {
        await refreshDrawsAndMeta()
      }
      actionLoading.value = false
    }

    async function onGenerateInto(toEventId) {
      actionLoading.value = true
      const raw = generateScores.value[toEventId]
      const score = raw != null ? Math.max(0, Number(raw)) : null
      const result = await generateInto(toEventId, score)
      generateScores.value = Object.assign({}, generateScores.value, { [toEventId]: null })
      if (result && result.message) {
        if (result.type === 'success') alert.showSuccess(result.message)
        else alert.showError(result.message)
      }
      if (result && result.type === 'success') {
        goToGeneratedRound(result.round != null ? result.round : activeRound.value, toEventId)
      } else {
        await refreshDrawsAndMeta()
      }
      actionLoading.value = false
    }

    async function onGenerateRunOff() {
      actionLoading.value = true
      const result = await generateRunOff()
      if (result && result.message) {
        if (result.type === 'success') alert.showSuccess(result.message)
        else alert.showError(result.message)
      }
      if (result && result.type === 'success') {
        if (result.round != null) goToGeneratedRound(result.round)
        else await refreshDrawsAndMeta()
      }
      actionLoading.value = false
    }

    async function onRegenerate() {
      showRegenerateConfirm.value = false
      const result = await regenerateFirstRound()
      if (result != null && result.message != null) {
        if (result.type === 'success') {
          alert.showSuccess(result.message)
        } else {
          alert.showError(result.message)
        }
      }
      if (result != null && result.type === 'success') {
        await refreshDrawsAndMeta()
      }
    }

    async function onPublish() {
      showPublishConfirm.value = false
      const result = await publishRound()
      if (result != null && result.message != null) {
        if (result.type === 'success') {
          alert.showSuccess(result.message)
        } else {
          alert.showError(result.message)
        }
      }
    }

    async function onResetTotalScore() {
      showCleanSlateConfirm.value = false
      const result = await resetTotalScore()
      if (result != null && result.message != null) {
        if (result.type === 'success') {
          alert.showSuccess(result.message)
        } else {
          alert.showError(result.message)
        }
      }
    }

    async function onResendNotifications() {
      const result = await resendNotifications()
      if (result != null && result.message != null) {
        if (result.type === 'success') {
          alert.showSuccess(result.message)
        } else {
          alert.showError(result.message)
        }
      }
    }

    watch(showSwapNominations, (val) => {
      if (val) {
        swapFrom.value = null
        swapTo.value = null
        fetchSwapCandidates()
      }
    })

    async function onSwap() {
      if (!swapFrom.value || !swapTo.value) return
      if (swapFrom.value.id === swapTo.value.id) return
      const result = await swapDraws({
        draw_from: swapFrom.value.id,
        draw_to: swapTo.value.id
      })
      if (result != null && result.message != null) {
        if (result.type === 'success') {
          alert.showSuccess(result.message)
          showSwapNominations.value = false
        } else {
          alert.showError(result.message)
        }
      }
    }

    async function onRefund(refundType) {
      refundLoading.value = true
      const result = await refundDraws(refundType)
      refundLoading.value = false
      showRefundConfirm.value = false
      if (result != null && result.message != null) {
        if (result.type === 'success') {
          alert.showSuccess(result.message)
        } else {
          alert.showError(result.message)
        }
      }
    }

    function onRoundChange(round) {
      if (loading.value || actionLoading.value || isMarking.value) return
      const roundData = rounds.value.find(r => r.index === round - 1)
      if (roundData && roundData.eventId && roundData.eventId !== Number(eventId.value)) {
        router.push(`/draws/${draftId.value}/${roundData.eventId}/${round}`)
        return
      }
      setRound(round)
    }

    function doScratch(draw, notify) {
      const status = draw.is_active ? 'scratched' : 'accepted'
      handleRowAction(draw.id, updateStatus(draw.id, { status, event_id: eventId.value, notify }))
    }

    function onScratch(draw) {
      if (draw.is_active && showNotificationsModal.value) {
        pendingScratchDraw.value = draw
        showScratchConfirm.value = true
      } else {
        doScratch(draw, false)
      }
    }

    function onScratchConfirm(notify) {
      const draw = pendingScratchDraw.value
      showScratchConfirm.value = false
      pendingScratchDraw.value = null
      if (draw) doScratch(draw, notify)
    }

    function onDragEnd(evt) {
      if (evt.oldIndex === evt.newIndex) return
      const list = filteredDraws.value
      const movedDraw = list[evt.oldIndex]
      if (movedDraw == null) return
      const targetDraw = list[evt.newIndex]
      if (targetDraw == null) return

      if (showNotificationsModal.value) {
        pendingMoveAction.value = { kind: 'reorder', drawId: movedDraw.id, newOrder: targetDraw.order }
        showMoveNotifyConfirm.value = true
      } else {
        handleRowAction(movedDraw.id, reorderDraw(movedDraw.id, targetDraw.order, false))
      }
    }

    function onMoveRequest(evt) {
      if (showNotificationsModal.value) {
        pendingMoveAction.value = { kind: 'move', drawId: evt.drawId, data: evt.data }
        showMoveNotifyConfirm.value = true
      } else {
        handleRowAction(evt.drawId, moveDraw(evt.drawId, { ...evt.data, notify: false }))
      }
    }

    function onMoveNotifyConfirm(notify) {
      const action = pendingMoveAction.value
      showMoveNotifyConfirm.value = false
      pendingMoveAction.value = null
      if (!action) return

      if (action.kind === 'reorder') {
        handleRowAction(action.drawId, reorderDraw(action.drawId, action.newOrder, notify))
      } else {
        handleRowAction(action.drawId, moveDraw(action.drawId, { ...action.data, notify }))
      }
    }

    let refundPollTimer = null
    function stopRefundPolling() {
      if (refundPollTimer) {
        clearInterval(refundPollTimer)
        refundPollTimer = null
      }
    }
    function startRefundPolling() {
      stopRefundPolling()
      refundPollTimer = setInterval(async () => {
        await fetchRefundStatus()
        if (!refundQueueNotEmpty.value) {
          stopRefundPolling()
          await refreshDrawsAndMeta()
        }
      }, 5000)
    }
    watch(refundQueueNotEmpty, (val) => {
      if (val) startRefundPolling()
      else stopRefundPolling()
    })

    watch(activeRound, () => {
      // Add/Restore and Swap forms hold selections/options scoped to the round they were
      // opened in (member/horse search, empty slots, swap candidates) — stale once the round
      // changes, so close them; they refetch fresh on next open.
      showAddNomination.value = false
      showSwapNominations.value = false
      const target = `/draws/${draftId.value}/${eventId.value}/${activeRound.value}`
      if (vm.proxy.$route.path !== target) router.replace(target)
      fetchAll(activeRound.value)
    })

    // Component instance is reused when only route params change (cross-event
    // navigation from round tabs or generate-into) — re-sync state manually
    watch(eventId, (val) => {
      if (val == null) return
      showAddNomination.value = false
      showSwapNominations.value = false
      const urlRound = Number(vm.proxy.$route.params.round) || 1
      if (activeRound.value !== urlRound) {
        activeRound.value = urlRound
      } else {
        fetchAll(activeRound.value)
      }
    })

    watch(redirectTo, (val) => {
      if (val == null) return
      if (val.path) {
        router.push(val.path).then(() => {
          if (val.message) EventBus.$emit('show-success', val.message)
        })
      } else if (val.round) {
        if (val.message) alert.showError(val.message)
        activeRound.value = val.round
      }
    })

    const loadMoreTrigger = ref(null)
    let loadMoreObserver = null
    let observingTrigger = false

    // Re-arm the observer only on a genuine scroll event, and unobserve as
    // soon as it fires — otherwise, right after a page is appended, the
    // sentinel can still sit inside rootMargin and the observer re-fires
    // immediately (chain-loading extra pages without the user scrolling).
    function armLoadMoreObserver() {
      if (loadMoreObserver == null || loadMoreTrigger.value == null || observingTrigger || !hasMore.value) return
      loadMoreObserver.observe(loadMoreTrigger.value)
      observingTrigger = true
    }

    // Appending rows below can otherwise shift the page (button/spinner
    // layout change, draggable re-render) — pin the scroll position so the
    // user keeps looking at exactly the same content they were on.
    async function loadMoreKeepingScroll() {
      const scrollY = window.scrollY
      await loadMore()
      await nextTick()
      window.scrollTo(window.scrollX, scrollY)
    }

    function onHttpError(message) {
      alert.showError(message)
    }

    onMounted(() => {
      fetchAll(activeRound.value)
      if (loadMoreTrigger.value != null) {
        loadMoreObserver = new IntersectionObserver((entries) => {
          if (!entries[0].isIntersecting) return
          loadMoreObserver.unobserve(loadMoreTrigger.value)
          observingTrigger = false
          if (hasMore.value && !loadingMore.value) loadMoreKeepingScroll()
        }, { root: null, threshold: 0 })
        armLoadMoreObserver()
        window.addEventListener('scroll', armLoadMoreObserver, { passive: true })
      }
      EventBus.$on('http-error', onHttpError)
      document.addEventListener('click', closeEventSwitch)
    })

    onBeforeUnmount(() => {
      if (loadMoreObserver != null) loadMoreObserver.disconnect()
      window.removeEventListener('scroll', armLoadMoreObserver)
      EventBus.$off('http-error', onHttpError)
      document.removeEventListener('click', closeEventSwitch)
      stopRefundPolling()
    })

    const downloadingPrint = ref(null)
    const downloadingCsv = ref(false)

    async function downloadPrint(type) {
      if (downloadingPrint.value != null) return
      downloadingPrint.value = type
      try {
        const res = await drawsApi.printPdf(eventId.value, activeRound.value, type)
        const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
        window.open(blobUrl, '_blank')
      } catch (_) {
        alert.showError('Failed to generate PDF.')
      } finally {
        downloadingPrint.value = null
        showPrintMenu.value = false
      }
    }

    async function downloadCsv() {
      if (downloadingCsv.value) return
      downloadingCsv.value = true
      try {
        const response = await drawsApi.exportCsv(eventId.value, activeRound.value)
        const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'draws.csv')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (_) {
        alert.showError('Failed to download CSV.')
      } finally {
        downloadingCsv.value = false
      }
    }

    function onPositiveIntKeydown(e) {
      const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End']
      if (!allowed.includes(e.key) && !/^\d$/.test(e.key)) e.preventDefault()
    }

    return {
      alert,
      draftId,
      eventId,
      activeRound,
      search,
      showConfirm,
      showAddNomination,
      showSwapNominations,
      rounds,
      roundTabItems,
      draws,
      emptyDrawsOrders,
      emptySlots,
      swapCandidatesLoading,
      swapFrom,
      swapTo,
      swapOptions,
      showPrintMenu,
      showRefundConfirm,
      refundLoading,
      existScratchedDraw,
      draftEvents,
      refundQueueNotEmpty,
      unpublishedDrawsCount,
      paymentMethods,
      disabledPaymentMethods,
      loading,
      loadingMore,
      hasMore,
      loadMoreTrigger,
      loadMoreKeepingScroll,
      eventName,
      judgesNum,
      displayEventName,
      showEventSwitch,
      eventSwitchTarget,
      nomMember,
      nomHorse,
      nomPlace,
      nomEvent,
      nomMemberOptions,
      nomHorseOptions,
      nomMemberLoading,
      nomMemberSearched,
      nomHorseLoading,
      nomSubmitting,
      nomPlaceOptions,
      nomEventOptions,
      showEventSelect,
      isEventSelectIgnored,
      nomEventHelpText,
      generateLabel,
      currentRoundType,
      isFinalRound,
      canResetTotalScore,
      hasTiedWinners,
      isCombinedNotSplit,
      childEvents,
      isParentSplit,
      finalEvent,
      finalRoundExists,
      calculatorId,
      calculator,
      lastRoundIndex,
      showRunOffPanel,
      showPublishConfirm,
      showRegenerateConfirm,
      showCleanSlateConfirm,
      onResetTotalScore,
      generateScore,
      generateScores,
      onFinaliseEvent,
      onGenerateNext,
      onGenerateInto,
      onPositiveIntKeydown,
      showScratchConfirm,
      onRoundChange,
      onScratch,
      onScratchConfirm,
      showMoveNotifyConfirm,
      onMoveRequest,
      onMoveNotifyConfirm,
      isFirstRoundInsert,
      addNomButtonLabel,
      nomMemberEmptyMessage,
      onMemberSearch,
      onMemberDropdownOpen,
      onMemberSelect,
      onAddNomination,
      filteredDraws,
      commitOptions,
      rowAlerts,
      actionLoading,
      isMarking,
      pendingMarks,
      handleRowAction,
      sortField,
      sortDir,
      toggleSort,
      isNextRoundGenerated,
      onMark,
      onGenerateRunOff,
      onRegenerate,
      onPublish,
      onResendNotifications,
      onSwap,
      onRefund,
      onDragEnd,
      setRound,
      markDraw,
      deleteDraw,
      moveDraw,
      swapDraws,
      cleanSlate,
      publishRound,
      updateStatus,
      loadMore,
      refreshDraws,
      refreshDrawsAndMeta,
      reorderDraw,
      updateCommit,
      togglePaymentStatus,
      scoreLimits,
      downloadPrint,
      downloadCsv,
      downloadingPrint,
      downloadingCsv
    }
  }
})
</script>

<style>
.draws-event-switch {
  position: relative;
  display: inline-block;
}

.draws-event-switch__btn {
  vertical-align: middle;
  font-size: 1.5rem;
}

.draws-event-switch__menu {
  position: absolute;
  left: 0;
  top: calc(100% + 4px);
  z-index: 1000;
  min-width: 280px;
  max-height: 340px;
  overflow-y: auto;
  margin: 0;
  padding: 0;
  list-style: none;
  background-color: #fff;
  box-shadow: 0 1px 4px #000;
}

.draws-event-switch__menu a {
  display: block;
  padding: 10px 15px;
  font-size: 1rem;
  white-space: nowrap;
}

.draws-event-switch__menu a:hover {
  background-color: var(--gray-100);
}

.draws-event-switch__item--active {
  font-weight: 700;
}

.draws-dashboard__warn-icon {
  display: inline-block;
  background-image: url('~@/assets/img/sprite.png');
  background-position: -34px 0px;
  width: 32px;
  height: 28px;
  vertical-align: middle;
  margin-right: 0.4rem;
}
</style>



// WEBPACK FOOTER //
// src/views/Draws/View/DrawsEventView.vue